/**
 * Customer Portal Dashboard.
 * Shows BHPH account summary, vehicle info, and payment history.
 * Self-service Phase A: schedule, payoff estimate, statements/receipts, requests. Payments are Phase B.
 */

import React, { useEffect, useState } from 'react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import {
  customerPortalApi,
  saveBlob,
  AccountSummary,
  PaymentHistoryItem,
} from '../../services/customerPortalApi';
import PaymentScheduleCard from './components/PaymentScheduleCard';
import PayoffQuoteCard from './components/PayoffQuoteCard';
import StatementsCard from './components/StatementsCard';
import MyRequestsCard from './components/MyRequestsCard';

const CustomerDashboardPage: React.FC = () => {
  const { customer, logout } = useCustomerAuth();

  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const downloadReceipt = async (paymentId: string) => {
    try {
      const blob = await customerPortalApi.downloadReceiptPdf(paymentId);
      saveBlob(blob, `receipt-${paymentId.slice(0, 8)}.pdf`);
    } catch {
      setError('Unable to download the receipt. Please try again later.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accts, pays] = await Promise.all([
          customerPortalApi.getAccount(),
          customerPortalApi.getPayments(),
        ]);
        setAccounts(accts);
        setPayments(pays);
      } catch (err) {
        setError('Unable to load account data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading your account...</p>
        </div>
      </div>
    );
  }

  const account = accounts[0]; // Primary account

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Welcome, {customer?.first_name}</h1>
            <p className="text-sm text-gray-500">Customer Portal</p>
          </div>
          <button
            onClick={logout}
            className="px-4 py-2 text-sm text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg hover:border-red-300 transition-colors"
          >
            Log Out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Account Summary Card */}
        {account ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h2 className="text-base font-semibold text-gray-900">Account Summary</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Balance */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">Total Balance</p>
                  <p className="text-3xl font-bold text-blue-900 mt-1">
                    $
                    {account.total_balance_amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>

                {/* Next Payment */}
                <div
                  className={`rounded-lg p-4 ${account.days_past_due > 0 ? 'bg-red-50' : 'bg-green-50'}`}
                >
                  <p
                    className={`text-sm font-medium ${account.days_past_due > 0 ? 'text-red-600' : 'text-green-600'}`}
                  >
                    {account.days_past_due > 0
                      ? `${account.days_past_due} Days Past Due`
                      : 'Next Payment'}
                  </p>
                  <p
                    className={`text-3xl font-bold mt-1 ${account.days_past_due > 0 ? 'text-red-900' : 'text-green-900'}`}
                  >
                    ${account.payment_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  {account.payment_due_date && (
                    <p className="text-sm mt-1 text-gray-500">
                      Due: {new Date(account.payment_due_date).toLocaleDateString()}
                    </p>
                  )}
                  {account.promise_date && (
                    <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Promise to pay: {new Date(account.promise_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Vehicle & Status */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Vehicle</p>
                  <p className="font-medium text-gray-900">{account.vehicle_year_make_model}</p>
                </div>
                <div>
                  <p className="text-gray-500">Account Status</p>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      account.account_status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {account.account_status}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Last Payment</p>
                  <p className="font-medium text-gray-900">
                    {account.last_payment_date
                      ? new Date(account.last_payment_date).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                {account.balance_late_amount > 0 && (
                  <div>
                    <p className="text-gray-500">Late Balance</p>
                    <p className="font-medium text-red-700">
                      $
                      {account.balance_late_amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            No active BHPH account found.
          </div>
        )}

        {account && (
          <>
            <PaymentScheduleCard accountUuid={account.id} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PayoffQuoteCard accountUuid={account.id} />
              <StatementsCard accountUuid={account.id} accountNumber={account.account_id} />
            </div>
            <MyRequestsCard accountUuid={account.id} />
          </>
        )}

        {/* Payment History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-base font-semibold text-gray-900">Payment History</h2>
          </div>

          {payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                      Principal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                      Interest
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payments.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {p.paid_date ? new Date(p.paid_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {p.paid_amount != null
                          ? `$${p.paid_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {p.payment_method || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                        {p.principal_applied_amt != null
                          ? `$${p.principal_applied_amt.toFixed(2)}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                        {p.interest_applied_amt != null
                          ? `$${p.interest_applied_amt.toFixed(2)}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => downloadReceipt(p.id)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Receipt
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No payment records found.</div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CustomerDashboardPage;
